import type { QueryClient } from '@tanstack/react-query';

// Centralised query keys for the groups feature. Imported from every
// hook + every mutation's onSuccess so invalidation paths can never
// drift from the read paths. Never inline a literal key string outside
// this module.

export type GroupListFilter = 'active' | 'archived' | 'all';

export const groupsKeys = {
  all: ['groups'] as const,
  capabilities: ['groups', 'capabilities'] as const,
  list: (filter: GroupListFilter, limit?: number, offset?: number, q?: string, tag?: string) =>
    ['groups', 'list', filter, limit ?? null, offset ?? null, q ?? null, tag ?? null] as const,
  detail: (slug: string) => ['groups', 'detail', slug] as const,
  fileSettings: (slug: string) => ['groups', 'file-settings', slug] as const,
  rules: (slug: string) => ['groups', 'rules', slug] as const,
  members: (slug: string) => ['groups', 'members', slug] as const,
  invites: (slug: string) => ['groups', 'invites', slug] as const,
  inviteRules: (slug: string) => ['groups', 'invite-rules', slug] as const,
  applications: (slug: string) => ['groups', 'applications', slug] as const,
  engagementSettings: (slug: string) => ['groups', 'engagement', slug, 'settings'] as const,
  engagementCapabilities: (slug: string) => ['groups', 'engagement', slug, 'capabilities'] as const,
  engagementPoolLinks: (slug: string) => ['groups', 'engagement', slug, 'pool-links'] as const,
  tasks: (slug: string) => ['groups', 'engagement', slug, 'tasks'] as const,
  pointsLedger: (slug: string) => ['groups', 'engagement', slug, 'ledger'] as const,
  pointsLeaderboard: (slug: string) => ['groups', 'engagement', slug, 'leaderboard'] as const,
  rewards: (slug: string) => ['groups', 'engagement', slug, 'rewards'] as const,
  redemptions: (slug: string) => ['groups', 'engagement', slug, 'redemptions'] as const,
  integrityReviews: (slug: string) => ['groups', 'engagement', slug, 'integrity-reviews'] as const,
};

export const engagementKeys = {
  myPoints: ['engagement', 'me', 'points'] as const,
};

export const inviteKeys = {
  preview: (token: string) => ['groups', 'invite-preview', token] as const,
};

/** Invalidate the entire groups subtree. Cheap for the user's first
 *  navigation — every query under `['groups', …]` becomes stale, the
 *  next render refetches what's actually visible. */
export function invalidateAllGroupsQueries(qc: QueryClient): Promise<void> {
  return qc.invalidateQueries({ queryKey: groupsKeys.all });
}

export function invalidateGroupMembershipDependents(qc: QueryClient, slug?: string): void {
  void qc.invalidateQueries({ queryKey: groupsKeys.all });
  if (slug !== undefined) {
    void qc.invalidateQueries({ queryKey: groupsKeys.detail(slug) });
    void qc.invalidateQueries({ queryKey: groupsKeys.members(slug) });
  }
  void qc.invalidateQueries({ queryKey: ['auth', 'me'] });
  void qc.invalidateQueries({ queryKey: ['coordination'] });
  void qc.invalidateQueries({ queryKey: ['surveys'] });
  void qc.invalidateQueries({ queryKey: ['calendar', 'list'] });
}
